// Plain-text ("ASCII") guitar tab export: one row per string, frets placed
// in columns spaced by rhythm (one dash per sixteenth), with chord names,
// sections, repeats and palm mutes on annotation rows, and the usual
// technique letters (h p / \ b r ~ x). Every fretted track is written; other
// instruments have no tab and are left out.

import { PPQ, barCapacity, ticks } from '@simplenotation/theory';
import type { Beat, Note, Score, Track } from '../model/types';
import { timeSigAt } from '../model/query';
import { fingerTrack } from './fingering';

export interface TextTabOptions {
  /** Maximum line length; whole bars wrap onto the next system (default 80). */
  width?: number;
  /** Append a legend of the symbols used and a credit line (default true). */
  legend?: boolean;
}

const SIXTEENTH = PPQ / 4;
const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const LEGEND: Record<string, string> = {
  h: 'h hammer-on',
  p: 'p pull-off',
  '/': '/ slide up',
  '\\': '\\ slide down',
  b: 'b bend (to the fret shown)',
  r: 'r release',
  '~': '~ vibrato',
  x: 'x dead note',
  '<>': '<n> natural harmonic',
  '[]': '[n] artificial harmonic',
  '()': '(n) ghost note',
  t: 't tap',
  PM: 'PM palm mute',
};

interface Column {
  /** Sort key: tick, with grace notes just before their main beat. */
  key: number;
  tick: number;
  cells: Map<number, string>;
  chord?: string;
  palmMute: boolean;
}

interface Rendered {
  /** Per string (index 0 = string 1, the highest), the bar's characters after the opening '|'. */
  rows: string[];
  chords: string;
  marks: string;
  pm: string;
  width: number;
}

/** String names, high → low; the first string is lower-case when its name repeats (e B G D A E). */
export function stringNames(tuning: readonly number[]): string[] {
  const names = tuning.map((m) => NAMES[((m % 12) + 12) % 12]);
  if (names.length > 1 && names.slice(1).includes(names[0])) names[0] = names[0].toLowerCase();
  const w = Math.max(...names.map((n) => n.length));
  return names.map((n) => n.padEnd(w));
}

function tempoText(score: Score, bar: number): string | null {
  const t = score.tempos.find((m) => m.bar === bar && m.beatOffsetTicks === 0);
  return t ? `${t.bpm} bpm` : null;
}

/** Text tab for every fretted track of the score. Throws when there is none. */
export function exportTextTab(score: Score, opts: TextTabOptions = {}): string {
  const width = Math.max(30, opts.width ?? 80);
  const tracks = score.tracks.filter((t) => t.tuning && t.tuning.length > 0 && t.instrument !== 'drums');
  if (!tracks.length) throw new Error('Text tab needs a guitar, bass or other fretted instrument in the score');

  const used = new Set<string>();
  const out: string[] = [];
  const m = score.meta;
  out.push(m.title || 'Untitled');
  if (m.subtitle) out.push(m.subtitle);
  const by = [m.artist, m.composer && m.composer !== m.artist ? m.composer : undefined].filter(Boolean).join(' / ');
  if (by) out.push(by);
  const firstTempo = score.tempos.find((t) => t.bar === 0) ?? score.tempos[0];
  if (firstTempo) out.push(`Tempo: ${firstTempo.bpm} bpm`);

  for (const original of tracks) {
    // Fill in any missing string/fret positions without touching the document.
    const track: Track = structuredClone(original);
    fingerTrack(track);
    const tuning = track.tuning!;
    const names = stringNames(tuning);
    out.push('');
    if (tracks.length > 1) out.push(`${track.name}`);
    out.push(`Tuning: ${[...names].reverse().map((n) => n.trim().toUpperCase()).join(' ')}`);
    if (track.capo) out.push(`Capo: fret ${track.capo}`);
    out.push('');
    const bars = score.bars.map((_, i) => renderBar(score, track, i, used));
    writeSystems(out, bars, names, width);
  }

  if (opts.legend !== false) {
    const legend = Object.keys(LEGEND)
      .filter((k) => used.has(k))
      .map((k) => LEGEND[k]);
    if (out[out.length - 1] !== '') out.push('');
    if (legend.length) out.push(...legend, '');
    out.push('Tab made with SimpleNotation (https://simplenotation.com)');
  }
  return out.join('\n') + '\n';
}

function writeSystems(out: string[], bars: Rendered[], names: string[], width: number): void {
  const prefix = names[0].length + 1; // "e|"
  let i = 0;
  while (i < bars.length) {
    let w = prefix;
    let j = i;
    while (j < bars.length && (j === i || w + bars[j].width + 1 <= width)) {
      w += bars[j].width + 1;
      j++;
    }
    const sys = bars.slice(i, j);
    const pad = ' '.repeat(prefix);
    const marks = pad + sys.map((b) => b.marks.padEnd(b.width + 1)).join('');
    const chords = pad + sys.map((b) => b.chords.padEnd(b.width + 1)).join('');
    if (marks.trim()) out.push(marks.trimEnd());
    if (chords.trim()) out.push(chords.trimEnd());
    names.forEach((n, s) => out.push(`${n}|${sys.map((b) => `${b.rows[s]}|`).join('')}`));
    const pm = pad + sys.map((b) => b.pm.padEnd(b.width + 1)).join('');
    if (pm.trim()) out.push(pm.trimEnd());
    out.push('');
    i = j;
  }
}

function renderBar(score: Score, track: Track, barIdx: number, used: Set<string>): Rendered {
  const info = score.bars[barIdx];
  const strings = track.tuning!.length;
  const cap = barCapacity(timeSigAt(score, barIdx));
  const tb = track.bars[barIdx];
  const cols = new Map<number, Column>();
  let content = 0;

  for (const voice of tb?.voices ?? []) {
    let tick = 0;
    voice.forEach((beat, bi) => {
      let key = tick;
      if (beat.grace) {
        // Grace notes sit just before the next main beat, in order.
        let after = 0;
        for (let k = bi + 1; k < voice.length && voice[k].grace; k++) after++;
        key = tick - (after + 1) / 64;
      }
      if (!beat.rest && beat.notes.length) {
        let col = cols.get(key);
        if (!col) cols.set(key, (col = { key, tick, cells: new Map(), palmMute: false }));
        for (const n of beat.notes) {
          if (n.string === undefined || n.fret === undefined || n.string < 1 || n.string > strings) continue;
          const text = noteText(track, n, beat, used);
          if (text) col.cells.set(n.string - 1, text);
        }
        if (beat.chord?.name && !col.chord) col.chord = beat.chord.name;
        if (beat.palmMute) {
          col.palmMute = true;
          used.add('PM');
        }
      } else if (beat.chord?.name) {
        let col = cols.get(key);
        if (!col) cols.set(key, (col = { key, tick, cells: new Map(), palmMute: false }));
        col.chord ??= beat.chord.name;
      }
      if (!beat.grace) tick += ticks(beat.duration);
    });
    content = Math.max(content, tick);
  }
  const barLen = Math.max(cap, content);
  const list = [...cols.values()].sort((a, b) => a.key - b.key);

  // Column widths: the widest fret text plus a dash, or one dash per sixteenth until the next column.
  const rows = Array.from({ length: strings }, () => '-');
  let chords = ' ';
  let pm = ' ';
  let pmOpen = false;
  list.forEach((c, k) => {
    const next = k + 1 < list.length ? list[k + 1].tick : barLen;
    const text = Math.max(1, ...[...c.cells.values()].map((s) => s.length));
    const w = Math.max(text + 1, Math.round((next - c.tick) / SIXTEENTH));
    const at = rows[0].length;
    for (let s = 0; s < strings; s++) rows[s] += (c.cells.get(s) ?? '').padEnd(w, '-');
    if (c.chord && chords.length <= at) chords = chords.padEnd(at) + c.chord + ' ';
    if (c.palmMute) {
      pm = pm.padEnd(at) + (pmOpen ? '-'.repeat(w) : 'PM'.padEnd(w, '-'));
      pmOpen = true;
    } else pmOpen = false;
  });
  if (!list.length) for (let s = 0; s < strings; s++) rows[s] += '-'.repeat(Math.max(3, Math.round(barLen / SIXTEENTH)));

  const marks: string[] = [];
  if (info.section) marks.push(`[${[info.section.letter, info.section.name].filter(Boolean).join(' ')}]`);
  if (info.endings?.length) marks.push(`${info.endings.join('.,')}.`);
  if (info.repeatStart) marks.push('|:');
  if (barIdx > 0) {
    const tempo = tempoText(score, barIdx);
    if (tempo) marks.push(tempo);
  }
  if (info.direction) marks.push(info.direction);
  let markText = marks.join(' ');
  if (info.repeatEnd) {
    const end = `:| x${info.repeatEnd}`;
    markText = markText ? `${markText} ${end}` : end;
  }
  const width = Math.max(rows[0].length, markText.length, chords.trimEnd().length, pm.trimEnd().length);
  return {
    rows: rows.map((r) => r.padEnd(width, '-')),
    chords: chords.trimEnd(),
    marks: markText,
    pm: pm.trimEnd(),
    width,
  };
}

/** The tab text for one note, with technique markers; empty for a tied continuation. */
function noteText(track: Track, n: Note, beat: Beat, used: Set<string>): string {
  if (isTieContinuation(track, n)) return '';
  const fret = n.fret!;
  let s = String(fret);
  if (n.dead) {
    s = 'x';
    used.add('x');
  } else if (n.harmonic === 'natural') {
    s = `<${fret}>`;
    used.add('<>');
  } else if (n.harmonic === 'artificial') {
    s = `[${fret}]`;
    used.add('[]');
  } else if (n.ghost) {
    s = `(${fret})`;
    used.add('()');
  }
  if (n.tap || beat.tap) {
    s = `t${s}`;
    used.add('t');
  }
  if (n.slide === 'inBelow') {
    s = `/${s}`;
    used.add('/');
  } else if (n.slide === 'inAbove') {
    s = `\\${s}`;
    used.add('\\');
  }
  if (n.bend && n.bend.points.length) {
    const peak = Math.max(...n.bend.points.map((p) => p.semis));
    const last = n.bend.points[n.bend.points.length - 1].semis;
    if (peak > 0) {
      s += `b${fret + Math.round(peak)}`;
      used.add('b');
      if (last < peak - 0.25) {
        s += `r${fret + Math.round(last)}`;
        used.add('r');
      }
    }
  }
  if (n.vibrato) {
    s += '~';
    used.add('~');
  }
  const next = nextOnString(track, n);
  if (n.hammerPull && next?.fret !== undefined) {
    const c = next.fret >= fret ? 'h' : 'p';
    s += c;
    used.add(c);
  } else if ((n.slide === 'legato' || n.slide === 'shift') && next?.fret !== undefined) {
    const c = next.fret >= fret ? '/' : '\\';
    s += c;
    used.add(c);
  } else if (n.slide === 'outUp') {
    s += '/';
    used.add('/');
  } else if (n.slide === 'outDown') {
    s += '\\';
    used.add('\\');
  }
  return s;
}

// ---- note order per string (for h/p and slide direction, and ties)

const orderCache = new WeakMap<Track, Map<string, { prev?: Note; next?: Note }>>();

function stringOrder(track: Track): Map<string, { prev?: Note; next?: Note }> {
  let map = orderCache.get(track);
  if (map) return map;
  map = new Map();
  const last = new Map<number, Note>();
  for (const tb of track.bars) {
    const events: { key: number; note: Note }[] = [];
    for (const voice of tb.voices) {
      let tick = 0;
      voice.forEach((beat, bi) => {
        const key = beat.grace ? tick - (voice.length - bi) / 1024 : tick;
        if (!beat.rest) for (const n of beat.notes) if (n.string !== undefined) events.push({ key, note: n });
        if (!beat.grace) tick += ticks(beat.duration);
      });
    }
    events.sort((a, b) => a.key - b.key);
    for (const { note } of events) {
      const prev = last.get(note.string!);
      if (prev) {
        map.get(prev.id)!.next = note;
        map.set(note.id, { prev });
      } else map.set(note.id, {});
      last.set(note.string!, note);
    }
  }
  orderCache.set(track, map);
  return map;
}

function nextOnString(track: Track, n: Note): Note | undefined {
  return stringOrder(track).get(n.id)?.next;
}

function isTieContinuation(track: Track, n: Note): boolean {
  const prev = stringOrder(track).get(n.id)?.prev;
  return !!prev?.tieToNext && prev.fret === n.fret;
}
